import React from "react";
import { ErrorCard, ViewPort } from "@weezevent/nacre";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { usePermissions } from "../../components/user/permissions";
import useOrganizationContext from "../../hooks/useOrganizationContext";
import { CAPACITIES } from "../../constants/capacities";
import logoError from "../../images/error-403.svg?url";
import Logo from "../../public/images/logo.svg?url";
import TopBarModules from "../../components/template/topBarModules/topBarModules";

const UnauthorizedPage = ({
	title = null,
	subTitle = null,
	backHomeUrl = null,
	refresh = true,
	errorCode = 403,
	shouldDisplayMyOrganizations = false,
	refetch = null
}) => {
	const { t } = useTranslation();
	const history = useHistory();
	const permissions = usePermissions();
	const { hasCapacity } = permissions || {};
	const organization = useOrganizationContext();

	const organizationId = organization?.id();

	const findAvailablePage = React.useCallback(() => {
		if (!hasCapacity || !organizationId) {
			return null;
		}

		const basePath = `/access/O${organizationId}`;

		// Check for checkpoints access
		const hasCheckpointsAccess =
			hasCapacity?.(CAPACITIES.CHECKLIST.READ, organizationId) ||
			hasCapacity?.(CAPACITIES.CHECKLIST.EDIT, organizationId) ||
			hasCapacity?.(CAPACITIES.DEFAULT.ADMIN, organizationId) ||
			hasCapacity?.(CAPACITIES.DEFAULT.OWNER, organizationId);

		if (hasCheckpointsAccess) {
			return `${basePath}/checkpoints`;
		}

		// Check for attendees access
		const hasAttendeesAccess =
			hasCapacity?.(CAPACITIES.ATTENDEES.MANAGE, organizationId) ||
			hasCapacity?.(CAPACITIES.ATTENDEES.RO, organizationId);

		if (hasAttendeesAccess) {
			return `${basePath}/attendees`;
		}

		// Check for events access
		const hasEventsAccess =
			hasCapacity?.(CAPACITIES.EVENT_CONFIG.READ, organizationId) ||
			hasCapacity?.(CAPACITIES.EVENT_CONFIG.MANAGE, organizationId) ||
			hasCapacity?.(CAPACITIES.ZONE.READ, organizationId) ||
			hasCapacity?.(CAPACITIES.ZONE.MANAGE, organizationId) ||
			hasCapacity?.(CAPACITIES.POPULATION.READ, organizationId) ||
			hasCapacity?.(CAPACITIES.POPULATION.MANAGE, organizationId);

		if (hasEventsAccess) {
			return `${basePath}/events`;
		}

		// If no permissions, redirect to product pages (admin URL)
		const adminUrl = window.weezConfig?.front?.weezeventAdminUrl;
		if (adminUrl) {
			return adminUrl;
		}

		return null;
	}, [hasCapacity, organizationId]);

	const availablePageUrl = React.useMemo(() => {
		if (backHomeUrl) {
			return backHomeUrl;
		}
		return findAvailablePage();
	}, [backHomeUrl, findAvailablePage]);

	const errorTitle = React.useMemo(() => {
		if (title) {
			return t(title);
		}
		if (shouldDisplayMyOrganizations) {
			return t("access.common.error.labelNoAccessToOrganization");
		}
		if (errorCode >= 500) {
			return t("access.common.error.labelServiceUnavailable");
		}
		return t("access.common.error.labelUnauthorized");
	}, [title, errorCode, shouldDisplayMyOrganizations, t]);

	const errorSubTitle = React.useMemo(() => {
		if (subTitle) {
			return t(subTitle);
		}
		if (errorCode >= 500) {
			return t("access.common.error.serviceUnavailableMessage");
		}
		return t("access.common.error.errorCodeLabel", { code: errorCode });
	}, [subTitle, errorCode, t]);

	const onBackHome = React.useCallback(() => {
		if (availablePageUrl) {
			if (availablePageUrl.startsWith("http")) {
				window.location.href = availablePageUrl;
			} else {
				history.push(availablePageUrl);
			}
		} else if (backHomeUrl) {
			history.push(backHomeUrl);
		}
	}, [history, backHomeUrl, availablePageUrl]);

	const onRefresh = React.useCallback(() => {
		window.location.reload();
	}, []);

	const contentStyle = {
		margin: "1em"
	};

	return (
		<ViewPort
			logo={Logo}
			onClickLogo={
				organizationId
					? () => history.push(`/access/O${organizationId}`)
					: null
			}
			topBarModules={<TopBarModules />}
			displayMenuButton={false}
		>
			<div style={contentStyle}>
				<ErrorCard
					image={logoError}
					title={errorTitle}
					subTitle={errorSubTitle}
					onRefresh={!refresh ? null : onRefresh}
					onBackHome={availablePageUrl ? onBackHome : null}
					labelRefresh={t("access.common.cta.refresh")}
					labelBackHome={
						availablePageUrl
							? t("access.common.cta.backHome")
							: null
					}
				/>
			</div>
		</ViewPort>
	);
};

UnauthorizedPage.propTypes = {
	title: PropTypes.string,
	subTitle: PropTypes.string,
	backHomeUrl: PropTypes.string,
	refresh: PropTypes.bool,
	errorCode: PropTypes.number,
	shouldDisplayMyOrganizations: PropTypes.bool,
	refetch: PropTypes.func
};

export default UnauthorizedPage;
